import { useCallback, useRef, useState } from "react";
import type { BillOut, CloseBillFiscalInvoiceIn } from "../../../../../api/bills";
import { isClosedEmptyBill } from "../schema/orderingFloorData";

/** "close" — обичайното закриване; "retry" — закрита сметка без касова
 *  бележка (`total = 0`): същият модал, заявката е към `/close/retry`. */
export type CloseBillMode = "close" | "retry";

export const closeModeOf = (bill: BillOut): CloseBillMode =>
  isClosedEmptyBill(bill) ? "retry" : "close";

/** Сметката, за която е отворен модалът „Закрий". */
export interface CloseBillTarget {
  billId: number;
  mode: CloseBillMode;
  /** Сметката в момента на отварянето — ако после изчезне от списъка,
   *  модалът не остава без име и сума (BE е авторитетът при самото закриване). */
  snapshot: BillOut;
  /** „Фактуриране" → отворена сметка → купувач: закриването издава фискална
   *  фактура. Живее в целта, тоест САМО за това отваряне на „Закрий" —
   *  следващото (F7, „Закрий") е обикновено. */
  fiscalInvoiceBuyer?: CloseBillFiscalInvoiceIn;
}

interface CloseBillTargetOptions {
  /** Избраната в момента сметка. */
  selectedBillId: number | null;
  /** Текущият `GET /bills`. */
  bills: BillOut[];
  /** Питането преди „Закрий" („Брой гости") — за ТАЗИ сметка;
   *  `false` = „Отказ" и модалът не се отваря. */
  askBeforeClose: (bill: BillOut) => Promise<boolean>;
  /** Докато отговаряше, сметката изчезна или смени състоянието си (закрита
   *  от друг терминал) — модалът не се отваря, нито за нея, нито за друга. */
  onTargetGone: (bill: BillOut) => void;
}

/**
 * Модалът „Закрий" е за ЕДНА сметка, фиксирана при натискането — не за
 * „избраната в момента". Между натискането и отварянето може да има въпрос
 * („Брой гости"), а дотогава опресняването или друг терминал може да махне
 * сметката от списъка и изборът да мине на друга маса. Затова:
 *
 * - id-то и състоянието на сметката (отворена / закрита без бон) се помнят
 *   при натискането, въпросът се задава за нея;
 * - след отговора тя трябва още да е в списъка и в СЪЩОТО състояние — иначе
 *   `onTargetGone` и нищо не се отваря (никога за друга сметка);
 * - модалът получава изричното id (`closeTarget.billId`), а не избраното —
 *   смяна на избора (или F7 за друга маса), докато е отворен, не го пренасочва.
 */
export const useCloseBillTarget = ({
  selectedBillId,
  bills,
  askBeforeClose,
  onTargetGone,
}: CloseBillTargetOptions) => {
  const [closeTarget, setCloseTarget] = useState<CloseBillTarget | null>(null);
  // Целта остава след затваряне (за анимацията на модала) — отворен ли е,
  // казва само този флаг.
  const [closeModalOpen, setCloseModalOpen] = useState(false);

  // Последният рендер — `requestClose` е стабилен (ефектите на TableFooter го
  // имат в зависимостите), а след `await` трябва да види ПРЕСНИЯ списък.
  const latestRef = useRef({
    selectedBillId,
    bills,
    askBeforeClose,
    onTargetGone,
    closeModalOpen,
  });
  latestRef.current = {
    selectedBillId,
    bills,
    askBeforeClose,
    onTargetGone,
    closeModalOpen,
  };

  const openFor = useCallback(async (fiscalInvoiceBuyer?: CloseBillFiscalInvoiceIn) => {
    const { selectedBillId: billId, bills: billsAtClick } = latestRef.current;
    // Вече отворен модал не се пренасочва (F7 върху него за друга маса).
    if (billId == null || latestRef.current.closeModalOpen) return;
    const billAtClick = billsAtClick.find((bill) => bill.id === billId);
    if (!billAtClick) return;
    const mode = closeModeOf(billAtClick);
    // Фискалната фактура е само за ОТВОРЕНА сметка — закрита междувременно
    // (повторен опит без бон) не наследява купувача.
    if (fiscalInvoiceBuyer && mode !== "close") {
      latestRef.current.onTargetGone(billAtClick);
      return;
    }

    if (!(await latestRef.current.askBeforeClose(billAtClick))) return;
    if (latestRef.current.closeModalOpen) return;

    const billNow = latestRef.current.bills.find((bill) => bill.id === billId);
    if (
      !billNow ||
      billNow.status !== billAtClick.status ||
      closeModeOf(billNow) !== mode
    ) {
      latestRef.current.onTargetGone(billAtClick);
      return;
    }
    setCloseTarget({
      billId,
      mode,
      snapshot: billNow,
      ...(fiscalInvoiceBuyer ? { fiscalInvoiceBuyer } : {}),
    });
    setCloseModalOpen(true);
  }, []);

  // Без аргумент — бутонът и F7 подават събитие, което не е купувач.
  const requestClose = useCallback(() => openFor(), [openFor]);
  const requestFiscalInvoiceClose = useCallback(
    (fiscalInvoiceBuyer: CloseBillFiscalInvoiceIn) => openFor(fiscalInvoiceBuyer),
    [openFor],
  );

  const dismissClose = useCallback(() => setCloseModalOpen(false), []);

  // Живите данни на целта (сума, клиент), докато е в списъка; иначе снимката.
  const closeTargetBill = closeTarget
    ? (bills.find((bill) => bill.id === closeTarget.billId) ?? closeTarget.snapshot)
    : null;

  return {
    closeTarget,
    closeTargetBill,
    closeModalOpen,
    requestClose,
    requestFiscalInvoiceClose,
    dismissClose,
  };
};
